import { useState, useEffect, useMemo } from 'react';
import { NSLabel, NSSelect } from 'bricks';
import NSInputNumberStepper from 'bricks/NSInputNumberStepper/NSInputNumberStepper';
import { IAssumptions, SaleFrequency } from 'views/ProFormaTable/types';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import { buildSalesStartOptions, resolveSalesStartOption, SalesStartOption } from '../helpers';
import './ProFormaExitSettings.scss';

const SALE_FREQUENCY_OPTIONS: { value: SaleFrequency; label: string }[] = [
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'monthly', label: 'Monthly' },
];

const DEFAULT_SALE_FREQUENCY: SaleFrequency = 'quarterly';
const DEFAULT_CLOSING_COSTS = 0.025;
const DEFAULT_ANNUAL_PRICE_ESCALATION = 0.03;

const ProFormaExitSettings = () => {
    const { disposition, proForma, uses, handleUpdateProFormaAssumptions } = useProFormaSocketContext();

    const [saleFrequency, setSaleFrequency] = useState<SaleFrequency>(DEFAULT_SALE_FREQUENCY);
    const [salesStart, setSalesStart] = useState<string | null>(null);
    const [closingCosts, setClosingCosts] = useState<number>(DEFAULT_CLOSING_COSTS);
    const [annualPriceEscalation, setAnnualPriceEscalation] = useState<number>(DEFAULT_ANNUAL_PRICE_ESCALATION);

    useEffect(() => {
        if (!disposition) return;
        if (disposition.saleFrequency) setSaleFrequency(disposition.saleFrequency);
        if (disposition.salesStart) setSalesStart(disposition.salesStart);
        if (disposition.closingCostsPercentage !== undefined && disposition.closingCostsPercentage !== null) {
            setClosingCosts(disposition.closingCostsPercentage);
        }
        if (
            disposition.annualPriceEscalationPercentage !== undefined
            && disposition.annualPriceEscalationPercentage !== null
        ) {
            setAnnualPriceEscalation(disposition.annualPriceEscalationPercentage);
        }
    }, [disposition]);

    const salesStartOptions = useMemo(
        () => buildSalesStartOptions(proForma?.constructionStart, uses?.developmentEndDate, saleFrequency),
        [proForma?.constructionStart, uses?.developmentEndDate, saleFrequency],
    );

    const selectedSalesStart = useMemo(
        () => resolveSalesStartOption(salesStartOptions, salesStart),
        [salesStartOptions, salesStart],
    );

    const labelClassName = 'ProFormaExitSettings__label my-auto mr-3 font-weight-normal text-dark text-nowrap';

    const updateAssumptions = (patch: Partial<IAssumptions>) => {
        handleUpdateProFormaAssumptions(patch);
    };

    return (
        <div className="ProFormaExitSettings px-3 py-3">
            <div className="d-flex align-items-center">
                <NSLabel className={labelClassName}>Sale frequency</NSLabel>
                <div className="ProFormaExitSettings__select">
                    <NSSelect
                        name="saleFrequency"
                        options={SALE_FREQUENCY_OPTIONS}
                        value={SALE_FREQUENCY_OPTIONS.find(option => option.value === saleFrequency)}
                        onChange={(option: { value: SaleFrequency; label: string } | null) => {
                            if (!option) return;
                            setSaleFrequency(option.value);
                            setSalesStart(null);
                            updateAssumptions({ saleFrequency: option.value });
                        }}
                        isClearable={false}
                        doNotShowErrorMsg
                    />
                </div>
            </div>

            <div className="d-flex align-items-center mt-2">
                <NSLabel className={labelClassName}>Sales start</NSLabel>
                <div className="ProFormaExitSettings__select">
                    <NSSelect
                        name="salesStart"
                        options={salesStartOptions}
                        value={selectedSalesStart}
                        onChange={(option: SalesStartOption | null) => {
                            const nextValue = option?.value ?? null;
                            setSalesStart(nextValue);
                            updateAssumptions({ salesStart: nextValue });
                        }}
                        placeholder="Select period"
                        isClearable={false}
                        doNotShowErrorMsg
                    />
                </div>
            </div>

            <div className="d-flex align-items-center mt-2">
                <NSLabel className={labelClassName}>Closing costs</NSLabel>
                <NSInputNumberStepper
                    max={100}
                    percentage
                    name="closingCostsPercentage"
                    value={closingCosts}
                    onChange={(value: number) => {
                        setClosingCosts(value);
                        updateAssumptions({ closingCostsPercentage: value });
                    }}
                    min={0}
                    step={0.1}
                    inputGroupClassName="ProFormaExitSettings__input-width"
                />
            </div>

            <div className="d-flex align-items-center mt-2">
                <NSLabel className={labelClassName}>Annual price escalation</NSLabel>
                <NSInputNumberStepper
                    max={100}
                    percentage
                    name="annualPriceEscalationPercentage"
                    value={annualPriceEscalation}
                    onChange={(value: number) => {
                        setAnnualPriceEscalation(value);
                        updateAssumptions({ annualPriceEscalationPercentage: value });
                    }}
                    min={0}
                    step={0.1}
                    inputGroupClassName="ProFormaExitSettings__input-width"
                />
            </div>
        </div>
    );
};

export default ProFormaExitSettings;
